import React, { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../hooks/ThemeContext";
import { UserContext } from "../hooks/UserContext";
import ContactForm from "../components/common/ContactForm";
import {
  Mail,
  MessageCircle,
  ArrowRight,
  Clock,
  Zap,
  Shield,
  User,
} from "lucide-react";

const CHANNELS = [
  {
    icon: Zap,
    title: "Réponse rapide",
    desc: "Premier retour sous 24h, souvent bien moins.",
    accent: "#f43f5e",
  },
  {
    icon: Shield,
    title: "Échange confidentiel",
    desc: "Vos informations restent strictement privées.",
    accent: "#a855f7",
  },
  {
    icon: Clock,
    title: "Suivi transparent",
    desc: "Chaque demande est tracée et traitée avec soin.",
    accent: "#6366f1",
  },
];

const Contact = () => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated } = useContext(UserContext);
  const location = useLocation();
  const isDark = theme === "dark";

  const g = "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)";
  const sub = isDark ? "text-gray-400" : "text-gray-500";
  const muted = isDark ? "text-gray-500" : "text-gray-400";

  const orderContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const isOrder = params.get("commande") === "1";
    const plan = params.get("plan") || "Site sur-mesure";
    const mode = params.get("mode") || "standard";
    const amount = Number(params.get("amount") || 0);

    if (!isOrder) {
      return {
        isOrder: false,
        submitLabel: "Envoyer le message",
        prefill: null,
      };
    }

    const isNoCommitment = mode === "no-commitment";
    const amountText =
      isNoCommitment && amount > 0
        ? `Montant: ${amount.toLocaleString("fr-FR")} EUR.`
        : "";

    return {
      isOrder: true,
      submitLabel: "Passer ma commande",
      plan,
      mode,
      amount,
      prefill: {
        service: plan,
        requestType: "creation",
        message:
          `Bonjour, je souhaite passer commande pour: ${plan}. ` +
          `${isNoCommitment ? "Option sans engagement." : ""} ${amountText}`.trim(),
      },
    };
  }, [location.search]);

  return (
    <div
      className="flex-1 flex flex-col"
      style={{ backgroundColor: isDark ? "#07070a" : "#f9f9fb" }}
    >
      {/* ── HERO ─────────────────────────────────────────────────── */}
      <div
        className={`relative overflow-hidden ${isDark ? "bg-[#07070a]" : "bg-white"}`}
      >
        {/* Orbs */}
        {isDark && (
          <div className="pointer-events-none absolute inset-0">
            <div
              className="absolute -left-64 -top-32 h-[50rem] w-[50rem] rounded-full blur-[200px]"
              style={{ background: "rgba(168,85,247,0.10)" }}
            />
            <div
              className="absolute right-0 top-0 h-[40rem] w-[40rem] rounded-full blur-[180px]"
              style={{ background: "rgba(244,63,94,0.08)" }}
            />
          </div>
        )}

        {/* Grid pattern */}
        {isDark && (
          <div
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        )}

        <div className="relative container mx-auto px-6 lg:px-10 pt-24 pb-20 max-w-7xl">
          <div className="max-w-2xl">
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.22em] border mb-8 ${
                isDark
                  ? "bg-white/[0.06] border-white/[0.10] text-gray-300"
                  : "bg-white border-gray-200 text-gray-500 shadow-sm"
              }`}
            >
              <MessageCircle
                className="w-3.5 h-3.5"
                style={{ color: "#f43f5e" }}
              />
              Contactez-moi
            </div>

            {/* Title */}
            <h1
              className={`text-5xl lg:text-6xl font-black leading-[1.06] mb-6 ${
                isDark ? "text-white" : "text-gray-950"
              }`}
              style={{ letterSpacing: "-0.04em" }}
            >
              Parlons de votre{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: g }}
              >
                projet.
              </span>
            </h1>

            {/* Subtitle */}
            <p className={`text-xl leading-relaxed font-light max-w-lg ${sub}`}>
              Une idée, un besoin, une question — décrivez-le, je reviens vers
              vous{" "}
              <span
                className={`font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}
              >
                dans les 24h.
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <div className="container mx-auto px-6 lg:px-10 py-16 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
          {/* ── Colonne gauche : info + canaux ─────────────────── */}
          <div className="flex flex-col gap-6">
            {/* Garanties */}
            <div className="flex flex-col gap-3">
              {CHANNELS.map(({ icon: Icon, title, desc, accent }) => (
                <div
                  key={title}
                  className={`flex items-start gap-4 p-5 rounded-2xl border transition-all ${
                    isDark
                      ? "bg-white/[0.03] border-white/[0.07] hover:border-white/[0.14]"
                      : "bg-white border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-md"
                  }`}
                >
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${accent}18` }}
                  >
                    <Icon className="w-4.5 h-4.5" style={{ color: accent }} />
                  </div>
                  <div>
                    <p
                      className={`text-sm font-bold mb-0.5 ${isDark ? "text-white" : "text-gray-900"}`}
                    >
                      {title}
                    </p>
                    <p className={`text-xs leading-relaxed ${muted}`}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Tarifs */}

            {/* Si connecté : accès créateur */}
            {isAuthenticated && (
              <div
                className={`rounded-2xl border p-5 ${
                  isDark
                    ? "border-white/[0.07] bg-white/[0.03]"
                    : "border-gray-100 bg-white shadow-sm"
                }`}
              >
                <p
                  className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}
                >
                  Projet sur-mesure
                </p>
                <p className={`text-sm mb-4 leading-relaxed ${sub}`}>
                  Commande personnalisée, Tarifsou devis détaillé ? Contactez
                  directement l'un de nos créateurs.
                </p>
              </div>
            )}

            {/* Si non connecté : invite login */}
            {!isAuthenticated && (
              <div
                className={`rounded-2xl border p-5 ${
                  isDark
                    ? "border-white/[0.07] bg-white/[0.03]"
                    : "border-gray-100 bg-white shadow-sm"
                }`}
              >
                <p
                  className={`text-xs font-bold uppercase tracking-widest mb-2 ${muted}`}
                >
                  Accès prioritaire
                </p>
                <p className={`text-sm leading-relaxed mb-4 ${sub}`}>
                  Connectez-vous pour accéder au support prioritaire, contacter
                  moi directement et suivez nos échanges.
                </p>
                <Link
                  to="/login"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:-translate-y-0.5 ${
                    isDark
                      ? "border-white/[0.12] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                      : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50 shadow-sm"
                  }`}
                >
                  Se connecter
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </div>

          {/* ── Colonne droite : formulaire ─────────────────────── */}
          <div
            className={`rounded-3xl border p-7 md:p-10 shadow-2xl ${
              isDark
                ? "bg-white/[0.04] border-white/[0.08]"
                : "bg-white border-gray-100 shadow-xl"
            }`}
          >
            <div className="mb-7">
              <p
                className={`text-xs font-bold uppercase tracking-widest mb-2 ${muted}`}
              >
                Formulaire
              </p>
              <h2
                className={`text-2xl font-black ${isDark ? "text-white" : "text-gray-900"}`}
                style={{ letterSpacing: "-0.02em" }}
              >
                Envoyez votre message
              </h2>
              <p className={`text-sm mt-1 ${sub}`}>
                Remplissez les champs ci-dessous — chaque détail nous aide à
                mieux vous répondre.
              </p>
            </div>
            {orderContext.isOrder && (
              <div
                className={`mb-6 rounded-2xl border p-4 ${
                  isDark
                    ? "border-rose-500/30 bg-rose-500/10"
                    : "border-rose-200 bg-rose-50"
                }`}
              >
                <p
                  className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                    isDark ? "text-rose-300" : "text-rose-700"
                  }`}
                >
                  Commande en cours
                </p>
                <p
                  className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {orderContext.plan}
                </p>
                {orderContext.mode === "no-commitment" &&
                  orderContext.amount > 0 && (
                    <p
                      className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Option sans engagement a{" "}
                      {orderContext.amount.toLocaleString("fr-FR")} EUR.
                    </p>
                  )}
              </div>
            )}
            <ContactForm
              variant="default"
              prefill={orderContext.prefill}
              submitLabel={orderContext.submitLabel}
              orderRequest={orderContext.isOrder ? orderContext : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
